import { type FC, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { skipToken } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useCreateBookingMutation,
  useCreateUserSchedulingLocationMutation,
  useGetAccountSchedulingSettingsQuery,
  useGetBookingByIdQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetUserSchedulingEventTypesQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
} from '../../../store/scheduling/scheduling'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { type Booking, type BookingClient, DeliveryMode, type Location, LocationKind, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { OwnerSelect } from './form/OwnerSelect'
import { EventTypeSelect } from './form/EventTypeSelect'
import { ClientSearch } from './form/ClientSearch'
import { InviteOthers } from './form/InviteOthers'
import { LocationSelect } from './form/LocationSelect'
import { SetTimeSection } from './form/SetTimeSection'
import { MeetingNameField } from './form/MeetingNameField'
import { bookingLocationFields, canManageOwnLocations, hasBookableCalendar, resolveLocationPreviewId, suggestMeetingTitle } from './form/scheduleHelpers'
import { SchedulePreviewColumn } from './SchedulePreviewColumn'
import { useCopyInvitationEmail } from './useCopyInvitationEmail'
import { SchedulingFlowHeader } from './SchedulingFlowHeader'
import { ScheduleSaveBar } from './ScheduleSaveBar'
import { formatTimezoneLabel } from '../shared/schedulingDisplay'
import { scheduleFormSchema, type ScheduleFormValues } from './scheduleFormSchema'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useDocumentTitle } from '../../../common/useDocumentTitle'

const FORM_ID = 'schedule-meeting-form'

interface ScheduleMeetingFormProps {
  eventTypes: SchedulingEventType[]
  accountLocations: Location[]
  accountDefaultLocationId?: string
  allowCustomLocation: boolean
  opportunity?: Booking
  prefillClient?: BookingClient
}

const ScheduleMeetingForm: FC<ScheduleMeetingFormProps> = ({
  eventTypes,
  accountLocations,
  accountDefaultLocationId,
  allowCustomLocation,
  opportunity,
  prefillClient,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [createBooking, { isLoading: isSending }] = useCreateBookingMutation()
  const [createUserLocation, { isLoading: isSavingLocation }] = useCreateUserSchedulingLocationMutation()
  const copyInvitationEmail = useCopyInvitationEmail()
  const [draftBooking, setDraftBooking] = useState<Booking | null>(null)

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
    shouldUnregister: false,
    // Prefill is loaded before this component mounts, so defaults initialize the form directly.
    defaultValues: {
      owner: authenticatedUser,
      eventType: (opportunity ? eventTypes.find((candidate) => candidate.id === opportunity.eventTypeId) : undefined) ?? null,
      clients: opportunity?.clients ?? (prefillClient ? [prefillClient] : []),
      timeMode: 'specific-time',
      slot: null,
      suggestedDate: null,
      meetingMode: opportunity?.meetingMode ?? MeetingMode.VIRTUAL,
      location: opportunity?.location ?? null,
      note: '',
      title: '',
      titleEdited: false,
      invitees: [],
      timezone: userTimezone,
      customLocation: null,
    },
  })

  const owner = useWatch({ control, name: 'owner' })
  const eventType = useWatch({ control, name: 'eventType' })
  const clients = useWatch({ control, name: 'clients' })
  const timeMode = useWatch({ control, name: 'timeMode' })
  const slot = useWatch({ control, name: 'slot' })
  const suggestedDate = useWatch({ control, name: 'suggestedDate' })
  const meetingMode = useWatch({ control, name: 'meetingMode' })
  const note = useWatch({ control, name: 'note' })
  const locationRef = useWatch({ control, name: 'location' })
  const title = useWatch({ control, name: 'title' })
  const titleEdited = useWatch({ control, name: 'titleEdited' })
  const invitees = useWatch({ control, name: 'invitees' })
  const timezone = useWatch({ control, name: 'timezone' })
  const customLocation = useWatch({ control, name: 'customLocation' })
  const client = clients[0]
  const validate = { shouldValidate: true, shouldDirty: true }

  const ownerId = owner?.id
  const ownerName = owner ? getUserDisplayName(owner) : ''
  const isBookingForOther = ownerId != null && ownerId !== authenticatedUser.id

  const { data: userLocations } = useGetUserSchedulingLocationsQuery(ownerId ? { accountId, userId: ownerId } : skipToken)
  const { data: userSettings, isFetching: isUserSettingsFetching } = useGetUserSchedulingSettingsQuery(ownerId ? { accountId, userId: ownerId } : skipToken)
  const { data: ownerEventTypeIds, isFetching: isOwnerEventTypesFetching } = useGetUserSchedulingEventTypesQuery(
    isBookingForOther ? { accountId, userId: ownerId } : skipToken,
  )

  // Booking on behalf of a sharer is limited to the event types on their booking page; booking for
  // yourself keeps the full account list.
  const ownerEventTypes = isBookingForOther ? eventTypes.filter((candidate) => (ownerEventTypeIds ?? []).includes(candidate.id)) : eventTypes
  const isOwnerCalendarBookable = userSettings ? hasBookableCalendar(userSettings) : false
  const isOwnerSetupLoading = isUserSettingsFetching || isOwnerEventTypesFetching

  useEffect(() => {
    if (titleEdited) {
      return
    }
    setValue('title', suggestMeetingTitle(clients, ownerName, eventType))
  }, [clients, ownerName, eventType, titleEdited, setValue])

  const allLocations = [...accountLocations, ...(userLocations ?? [])]
  const selectedSavedLocation = locationRef?.id ? allLocations.find((location) => location.id === locationRef.id) : undefined
  const effectiveLocationTimezone = customLocation?.timeZone || selectedSavedLocation?.timeZone || ''
  const locationTimezoneLabel = effectiveLocationTimezone ? formatTimezoneLabel(effectiveLocationTimezone) : ''

  const handleOwnerChange = (nextOwner: User) => {
    if (nextOwner.id === owner?.id) {
      return
    }
    setValue('owner', nextOwner, validate)
    // The owner determines the offered event types, locations and availability, so start those over
    setValue('eventType', null, validate)
    setValue('slot', null, validate)
    setValue('location', null, validate)
    setValue('customLocation', null, validate)
  }

  const handleEventTypeChange = (nextEventType: SchedulingEventType) => {
    const nextRef = nextEventType.location
    setValue('eventType', nextEventType, validate)
    setValue('slot', null, validate)
    setValue('meetingMode', nextEventType.meetingMode, validate)
    setValue(
      'location',
      nextRef ? { kind: nextRef.kind, id: resolveLocationPreviewId(nextRef, accountDefaultLocationId, userSettings?.defaults?.locationId) } : null,
      validate,
    )
    setValue('customLocation', null, validate)
  }

  const runCreate = async (data: ScheduleFormValues, delivery: DeliveryMode): Promise<Booking | null> => {
    if (!data.eventType || !data.owner || !isOwnerCalendarBookable) {
      return null
    }
    const isInPerson = data.meetingMode === MeetingMode.IN_PERSON
    let locationFields = bookingLocationFields(data.meetingMode, data.location)
    if (isInPerson && data.customLocation) {
      const saved = await createUserLocation({ accountId, userId: data.owner.id, input: data.customLocation })
      if ('error' in saved) {
        dispatch(addToast({ variant: 'error', title: 'Could not save the location', messages: ['Please try again.'] }))
        return null
      }
      locationFields = { location: { kind: LocationKind.FIXED, id: saved.data.id } }
    }
    const result = await createBooking({
      accountId,
      payload: {
        clients: data.clients,
        userId: data.owner.id,
        eventTypeId: data.eventType.id,
        ...locationFields,
        start: data.timeMode === 'specific-time' ? data.slot?.startAt : undefined,
        note: data.note || undefined,
        title: data.title || undefined,
        timezone: data.timeMode === 'specific-time' && !isInPerson ? data.timezone || undefined : undefined,
        additionalInvitees: data.invitees.length > 0 ? data.invitees : undefined,
        opportunityId: opportunity?.bookingId,
        delivery,
      },
    })
    if ('error' in result) {
      dispatch(
        opportunity
          ? addToast({ variant: 'info', title: 'Not available yet', messages: ['Scheduling from an opportunity is not supported yet.'] })
          : addToast({ variant: 'error', title: 'Could not create the booking', messages: ['Please try again.'] }),
      )
      return null
    }
    return result.data
  }

  const ensureDraftBooking = async (data: ScheduleFormValues): Promise<Booking | null> => {
    if (draftBooking) {
      return draftBooking
    }
    const booking = await runCreate(data, DeliveryMode.DRAFT)
    if (booking) {
      setDraftBooking(booking)
    }
    return booking
  }

  const onSend = handleSubmit(async (data) => {
    const booking = await runCreate(data, DeliveryMode.SEND)
    if (!booking) {
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Invitation sent', messages: [`${booking.title ?? 'The booking'} is now awaiting the client's reply.`] }))
    navigate('/scheduling/waiting')
  })

  const onCopyLink = handleSubmit(async (data) => {
    const booking = await ensureDraftBooking(data)
    if (!booking) {
      return
    }
    const link = booking.clients.find((candidate) => candidate.inviteUrl)?.inviteUrl
    if (!link) {
      dispatch(addToast({ variant: 'error', title: 'No booking link available', messages: ['The booking was saved — copy the link from Waiting.'] }))
      navigate('/scheduling/waiting')
      return
    }
    try {
      await navigator.clipboard.writeText(link)
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy the link', messages: ['The booking was saved — copy the link from Waiting.'] }))
      navigate('/scheduling/waiting')
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Booking link copied', messages: ['Paste it anywhere to share with the client.'] }))
    navigate('/scheduling/waiting')
  })

  const onCopyEmail = handleSubmit(async (data) => {
    const booking = await ensureDraftBooking(data)
    if (!booking || !data.eventType) {
      return
    }
    const link = booking.clients.find((candidate) => candidate.inviteUrl)?.inviteUrl
    if (!link) {
      dispatch(addToast({ variant: 'error', title: 'No booking link available', messages: ['The booking was saved — copy the link from Waiting.'] }))
      return
    }
    await copyInvitationEmail({
      eventType: data.eventType,
      client: data.clients[0],
      ownerName: data.owner ? getUserDisplayName(data.owner) : '',
      bookingUrl: link,
    })
  })

  const onPushToDraft = handleSubmit(async (data) => {
    const booking = await ensureDraftBooking(data)
    if (!booking) {
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Pushed to draft', messages: ['Find the invitation in your email drafts to review and send.'] }))
    navigate('/scheduling/waiting')
  })

  return (
    <>
      <Row>
        <Col lg={6} className='d-flex flex-column gap-4'>
          <form id={FORM_ID} className='d-flex flex-column gap-4' onSubmit={onSend}>
            <div className='d-flex flex-column gap-3 p-3 border zocks-border-color-gray-200 zocks-border-1.0'>
              <OwnerSelect onOwnerChange={handleOwnerChange} />
              {isOwnerSetupLoading ? (
                <div className='paragraph-small zocks-text-gray-500'>Checking {isBookingForOther ? `${ownerName}'s` : 'your'} scheduling setup…</div>
              ) : userSettings && !isOwnerCalendarBookable ? (
                <div className='d-flex align-items-center zocks-gap-0.25 paragraph-small zocks-text-warning-dark'>
                  <i className='bi bi-exclamation-triangle-fill' aria-hidden='true' />
                  {isBookingForOther
                    ? `${ownerName}'s calendar isn't ready for booking, so you can't book on their behalf yet.`
                    : 'Connect or reconnect your calendar to book meetings.'}
                </div>
              ) : null}
              <EventTypeSelect eventTypes={ownerEventTypes} selectedEventTypeId={eventType?.id ?? null} onEventTypeChange={handleEventTypeChange} />
              <ClientSearch
                clients={clients}
                ownerId={isBookingForOther ? ownerId : undefined}
                onClientsChange={(value) => setValue('clients', value, validate)}
              />
              <InviteOthers invitees={invitees} onInviteesChange={(value) => setValue('invitees', value, validate)} />
            </div>
            {eventType ? (
              <>
                {meetingMode === MeetingMode.IN_PERSON ? (
                  <LocationSelect
                    eventType={eventType}
                    accountDefaultLocationId={accountDefaultLocationId}
                    userDefaultLocationId={userSettings?.defaults?.locationId}
                    accountLocations={accountLocations}
                    userLocations={userLocations ?? []}
                    ownerUserId={ownerId ?? ''}
                    ownerFirstName={(owner?.name ?? '').split(' ')[0]}
                    isSchedulingForSelf={ownerId === authenticatedUser.id}
                    location={locationRef}
                    customLocation={customLocation}
                    allowCustomLocation={allowCustomLocation}
                    onChange={(next) => {
                      if (next.location !== undefined) {
                        setValue('location', next.location, validate)
                      }
                      if (next.customLocation !== undefined) {
                        setValue('customLocation', next.customLocation, validate)
                      }
                    }}
                  />
                ) : null}
                <SetTimeSection
                  mode={timeMode}
                  eventTypeId={eventType.id}
                  userId={ownerId}
                  meetingMode={meetingMode}
                  timezone={timezone}
                  locationTimezoneLabel={locationTimezoneLabel}
                  selectedSlot={slot}
                  suggestedDate={suggestedDate}
                  onModeChange={(value) => setValue('timeMode', value, validate)}
                  onSlotChange={(value) => setValue('slot', value, validate)}
                  onSuggestedDateChange={(value) => setValue('suggestedDate', value, validate)}
                  onTimezoneChange={(value) => setValue('timezone', value, validate)}
                />
                <MeetingNameField
                  title={title}
                  onTitleChange={(value) => {
                    setValue('title', value, validate)
                    setValue('titleEdited', true, validate)
                  }}
                />
              </>
            ) : null}
          </form>
        </Col>
        <Col lg={6}>
          <SchedulePreviewColumn
            eventType={eventType ?? undefined}
            mode={timeMode}
            emptyState={
              <div className='d-flex align-items-center justify-content-center text-center p-4 border zocks-border-color-gray-200 zocks-border-1.0 paragraph-medium zocks-text-gray-500'>
                Fill in the details to see a preview of what the client gets.
              </div>
            }
            ownerName={ownerName}
            invitees={invitees}
            meetingMode={meetingMode}
            slot={slot}
            title={title}
            timezoneLabel={meetingMode === MeetingMode.IN_PERSON ? locationTimezoneLabel : timezone ? formatTimezoneLabel(timezone) : ''}
            location={customLocation ?? selectedSavedLocation ?? null}
            note={note}
            onNoteChange={(value) => setValue('note', value, validate)}
            client={client}
            onEditEmail={() => navigate('/scheduling/setup')}
            onCopyEmail={onCopyEmail}
            copyEmailDisabled={!isValid || !isOwnerCalendarBookable || isSending || isSavingLocation}
          />
        </Col>
      </Row>
      <ScheduleSaveBar
        formId={FORM_ID}
        mode={timeMode}
        isValid={isValid && isOwnerCalendarBookable}
        isSubmitting={isSending || isSavingLocation}
        onCopyLink={onCopyLink}
        onPushToDraft={onPushToDraft}
      />
    </>
  )
}

export const ScheduleMeetingFlow: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const opportunityId = searchParams.get('opportunityId')
  const prefillContactId = searchParams.get('contactId')

  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const eventTypesQuery = useGetSchedulingEventTypesQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const opportunityQuery = useGetBookingByIdQuery(opportunityId ? { accountId, bookingId: opportunityId } : skipToken)
  const prefillContactQuery = useGetExternalUserByIdQuery(prefillContactId ? { accountId, userId: prefillContactId } : skipToken)

  useDocumentTitle('Scheduling | Zocks')

  // The form initializes its state from the prefill data, so wait for everything it needs before
  // mounting it. Key off the queries' loading flags (not data presence) so an errored lookup falls
  // through to an empty form instead of leaving the spinner up forever.
  const eventTypes = eventTypesQuery.data
  const isPrefillPending = Boolean(
    eventTypesQuery.isLoading || (opportunityId && opportunityQuery.isLoading) || (prefillContactId && prefillContactQuery.isLoading),
  )

  const prefillUser = prefillContactQuery.data?.data
  const prefillClient: BookingClient | undefined = prefillUser
    ? { contactId: prefillUser.id, firstName: prefillUser.name, email: prefillUser.email ?? '' }
    : undefined

  return (
    <Container>
      <SchedulingFlowHeader title='Schedule a meeting' onBack={() => navigate('/scheduling/requests')} onClose={() => navigate('/scheduling/requests')} />
      {isPrefillPending ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : (
        <ScheduleMeetingForm
          eventTypes={eventTypes ?? []}
          accountLocations={locations ?? []}
          accountDefaultLocationId={accountSettings?.defaults?.locationId}
          allowCustomLocation={canManageOwnLocations(accountSettings?.userControls)}
          opportunity={opportunityQuery.data}
          prefillClient={prefillClient}
        />
      )}
    </Container>
  )
}
